"use client";

import { useLayoutEffect, useRef } from "react";
import { Knopf } from "../../knopf";
import { SymGriff, SymPlus } from "./bearbeiten-symbole";

/** Zustand des Blockgriffs; gleiche Form wie `HornBlockgriffZustand` aus `@horn/editor` (`useHornBlockgriff`). */
export interface EditorBlockgriffZustand {
  rechteck: { left: number; top: number; right: number; bottom: number } | null;
}

/** Befehle des Blockgriffs; gleiche Form wie `HornBlockgriffBefehle`. */
export interface EditorBlockgriffBefehle {
  darunterEinfuegen: () => void;
  ziehenBeginnt: (dt: DataTransfer | null) => void;
}

/** Marker, an dem der Editor den Griff erkennt (gleich `BLOCKGRIFF_ATTRIBUT` in `@horn/editor`): der Zeiger darf auf den Griff wechseln. */
const GRIFF_ATTRIBUT = { "data-editor-blockgriff": "" } as const;

// Der Ziehgriff ist ein Ziehelement (`draggable`), kein Knopf: ein `button` lässt sich in Firefox nicht ziehen. Er trägt darum
// bewusst keine Knopf-Kette; sein Name steht im `title` (kein Tastaturweg zum Verschieben, wie in der Altfläche).
const ZIEHGRIFF = "inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground-1 hover:text-foreground cursor-grab";
const LEISTE = "fixed top-0 start-0 z-60 flex items-center";
const ABSTAND = 4;

/**
 * Blockgriff (Paket E-W2, G-W4: Lucide `GripVertical`, nur im Bearbeiten-Modus, Abweichung AW-064 (3)): steht links am Block unter dem Zeiger, zwei Teile:
 * Ziehgriff „Block verschieben“ und Knopf „Block darunter einfügen“. Auf Geräten ohne Zeiger-Hover (Touch) und im Lesemodus gibt es
 * ihn nicht (das entscheidet `useHornBlockgriff`, bei `null` rendert der Baustein nichts). Der Baustein prüft keine Rechte.
 */
export function EditorBlockgriff({ griff }: { griff: { zustand: EditorBlockgriffZustand; befehle: EditorBlockgriffBefehle } | null }) {
  const huelle = useRef<HTMLDivElement>(null);
  const rechteck = griff?.zustand.rechteck ?? null;

  // Platzieren vor dem ersten Zeichnen: links neben dem Block, am Fensterrand geklemmt. Die Koordinaten setzt der Baustein zur
  // Laufzeit am Element (wie `dropdown-menue.tsx`); sie sind keine Gestaltung.
  useLayoutEffect(() => {
    const h = huelle.current;
    if (!h || !rechteck) return;
    const b = h.getBoundingClientRect();
    h.style.left = `${Math.round(Math.max(ABSTAND, rechteck.left - b.width - ABSTAND))}px`;
    h.style.top = `${Math.round(rechteck.top + ABSTAND)}px`;
  });

  if (!griff || !rechteck) return null;
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-editor-and-toolbar-menus/floating-canvas-toolbar */}
      <div ref={huelle} className={LEISTE} {...GRIFF_ATTRIBUT} onMouseDown={(e) => e.preventDefault()}>
        <div
          className={ZIEHGRIFF}
          draggable
          title="Block verschieben"
          aria-hidden="true"
          onDragStart={(e) => griff.befehle.ziehenBeginnt(e.dataTransfer)}
        >
          <SymGriff />
        </div>
        <Knopf
          nurSymbol
          groesse="xs"
          variante="ghost"
          label="Block darunter einfügen"
          titel="Block darunter einfügen"
          symbol={<SymPlus />}
          starr
          onClick={griff.befehle.darunterEinfuegen}
        />
      </div>
    </>
  );
}
